import { OrderLedger } from '@omneo/omneo-sdk'

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { useAccountLayoutContext } from './AccountProfileContext'
import { AccountSectionWrapper } from './AccountSectionWrapper'
import { ImageGalleryLimit } from './ImageGalleryLimit'
import { Suspense } from './Suspense'
import { formatMoney } from '@shopify/theme-currency'
import { AccountTransaction } from './AccountTransaction'
import { PaginateHeader } from './PaginateHeader'
import { AccountOrderBadge } from './AccountOrderBadge'
import { FieldOption } from './FieldOption'
import { AccountBannerTransitions } from './AccountBannerTransaction'
import { accountTranslations } from '../helpers/translations'
const SHOPIFY_ORDERS_PER_PAGE = 20

/**
 * @typedef {{orders: OrderLedger[], selectedOrder: OrderLedger | null}} OrderPage
 */

const PRODUCT_IMAGE = 'https://cdn.shopify.com/s/files/1/0690/5538/1801/files/4-501874.jpg?v=1720590706&width=960'

const TransactionRow = ({ transaction, images, handleViewOrder, status }) => {
  const orderNumberTitle = `#${transaction.name || transaction.order_number || transaction.receipt_ref}`
  const orderDate = new Date(transaction.created_at).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  const total = formatMoney(transaction.total * 100)

  let tracking = null
  if (transaction.isShopifyOrder) {
    transaction.items.forEach((i) => {
      if (i.fulfillment.length) {
        tracking = i.fulfillment.find((f) => f.tracking_number && f.tracking_url) || null
      }
    })
  }

  return (
    <div className={'border-b border-solid py-10'}>
      <div className={'flex flex-col justify-between md:flex-row'}>
        <div className={'paragraph-md flex gap-2 font-bold md:hidden md:pb-0'}>
          <div className={'flex-0'}>{orderNumberTitle}</div>
          <div className={'flex-0'}>
            <AccountOrderBadge fulfillmentStatus={status} />
          </div>
        </div>
        <div data-role="order-info" className={'flex flex-row justify-between gap-4 md:flex-col md:justify-normal'}>
          <div data-role="order-details" className={'flex flex-col'}>
            <div className={'paragraph-md mb-4 flex hidden items-center gap-2 pb-6 font-bold md:flex md:pb-0'}>
              <div className={'flex-0'}>{orderNumberTitle}</div>
              <div className={'flex-0'}>
                <AccountOrderBadge fulfillmentStatus={status} />
              </div>
            </div>
            <div className={'paragraph-sm'}>{accountTranslations.dashboard.orders.order_date}: {orderDate}</div>
            <div className={'paragraph-sm'}>{accountTranslations.dashboard.orders.total}: {total}</div>
            {tracking && (
              <div className={'paragraph-sm'}>
                {accountTranslations.dashboard.orders.tracking_no}:{' '}
                <a href={tracking.tracking_url} target="_blank" rel="noopener noreferrer">
                  {tracking.tracking_number}
                </a>
              </div>
            )}
          </div>
          <div
            onClick={() => {
              handleViewOrder(transaction.id)
            }}
            data-role="order-view"
            className={'mt-auto md:mt-0'}
          >
            <div className={'btn btn-outline-black btn-sm w-fit'}>{accountTranslations.dashboard.orders.view_order}</div>
          </div>
        </div>
        <div data-role="image-gallery" className={'grid grid-cols-2 gap-4 pt-4'}>
          <ImageGalleryLimit array={images.map((image) => ({ img: image }))} renderLimit={2} />
        </div>
      </div>
    </div>
  )
}

const getProductImages = (transaction) => {
  const items = transaction.items
  const images = items.reduce((acc, item) => {
    if (!item.product_images.length) return acc
    const images = item.product_images.map(({ url }) => url)
    return acc.concat(images)
  }, [])
  if (!images.length) return [PRODUCT_IMAGE]
  return images
}

/**
 *
 * @param {*} param0
 * @returns
 */
const TransactionList = ({ transactions, shopifyTransactions, handleViewOrder, renderLimit }) => {
  const [orderType, setOrderType] = useState('fulfilled')
  const [currentPage, setCurrentPage] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const page = urlParams.get('page')
    return page ? parseInt(page) : 1
  })

  const amountOfPages = Math.ceil(accountGlobal.orders_count / SHOPIFY_ORDERS_PER_PAGE)

  // Record the first and last shopify transaction for the current page
  useEffect(() => {
    const sortedShopifyTransactions = shopifyTransactions.sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at)
    })

    const firstShopifyTransaction = sortedShopifyTransactions[0]
    const lastShopifyTransaction = sortedShopifyTransactions[sortedShopifyTransactions.length - 1]

    const existingData = JSON.parse(localStorage.getItem('orders-shopify-transaction') ?? '{}')

    localStorage.setItem(
      'orders-shopify-transaction',
      JSON.stringify({
        data: [
          {
            page: currentPage,
            first: firstShopifyTransaction,
            last: lastShopifyTransaction
          },
          ...(existingData?.data ? existingData.data.filter((d) => d.page !== currentPage) : [])
        ]
      })
    )
  }, [shopifyTransactions, currentPage])

  const filteredTransactions = useMemo(() => {
    const existingData = JSON.parse(localStorage.getItem('orders-shopify-transaction') ?? '{}')

    // Filter the transactions based on the fulfillment status
    let tx = transactions.filter((t) => {
      if (t.isShopifyOrder && !['fulfilled', 'complete'].includes(t.fulfillment_status_label ?? t.status)) {
        return orderType === 'pending'
      }
      return orderType === 'fulfilled'
    })

    // Filter the transactions based on the current page
    // Limit the transactions from the last shopify transaction from the previous page
    tx = tx.filter((t) => {
      // Handle the first page
      if (currentPage === 1) {
        if (shopifyTransactions.length <= 1 || amountOfPages === 1) return true
        return new Date(t.created_at) >= new Date(shopifyTransactions[shopifyTransactions.length - 1].created_at)
      }

      const lastShopifyTransaction = existingData.data.find((d) => d.page === currentPage - 1)?.last

      // Handle the last page
      if (shopifyTransactions.length === 1 || currentPage === amountOfPages) {
        return new Date(t.created_at) < new Date(lastShopifyTransaction.created_at)
      }

      // Handle the middle pages
      return (
        new Date(t.created_at) < new Date(lastShopifyTransaction.created_at) &&
        new Date(t.created_at) >= new Date(shopifyTransactions[shopifyTransactions.length - 1].created_at)
      )
    })

    // Render limit
    if (renderLimit) {
      return tx.slice(0, renderLimit)
    }

    return tx
  }, [transactions, orderType, currentPage])

  const handleNextPage = useCallback(
    (action) => {
      if (action === 'next' && currentPage < amountOfPages) {
        window.location = `?section=orders&page=${currentPage + 1}`
      }
      if (action === 'previous' && currentPage > 1) {
        window.location = `?section=orders&page=${currentPage - 1}`
      }
    },
    [currentPage, amountOfPages]
  )

  return (
    <div>
      <PaginateHeader
        props={{
          index: currentPage - 1,
          handleNextPage,
          renderLimit,
          pageMin: 0,
          pageMax: amountOfPages - 1
        }}
      />
      <div className={'max-w-[360px]'}>
        <FieldOption
          {...{
            name: 'order_type',
            value: orderType,
            onChange: (e) => setOrderType(e.target.value),
            options: [
              {
                label: 'Completed orders',
                value: 'fulfilled'
              },
              {
                label: 'Pending orders',
                value: 'pending'
              }
            ]
          }}
        />
      </div>
      <div className={'flex flex-col'}>
        {filteredTransactions.length === 0 && <p className="my-4 paragraph-md">No {orderType} orders.</p>}
        {filteredTransactions.map((transaction) => {
          const images = getProductImages(transaction)
          return (
            <TransactionRow
              transaction={transaction}
              images={images}
              handleViewOrder={handleViewOrder}
              status={orderType}
            />
          )
        })}
      </div>
    </div>
  )
}

const NoOrders = () => {
  return <p className="font-bold paragraph-lg">{accountTranslations.dashboard.orders.no_orders}</p>
}

/**
 *
 * Account Orders
 *
 *
 *
 * @param {{renderLimit?: number}} param0
 * @returns
 */
export const AccountOrders = ({ renderLimit, displayBanner = true, asSection = false }) => {
  const { config, omneoClient, handPushChildState, handlePushState, cacheConfig } = useAccountLayoutContext()

  /**
   * @type {[OrderPage, React.Dispatch<React.SetStateAction<OrderPage>>]}
   */
  const [pageData, setPageData] = useState({
    orders: cacheConfig.cacheData.Transactions ?? [],
    selectedOrder: null,
    unassignedItems: cacheConfig.cacheData.UnassignedItems ?? []
  })

  /**
   * @returns {boolean}
   */
  const displayViewAllOrdersButton = useMemo(() => {
    return asSection && !!cacheConfig.cacheData?.Transactions?.length
  }, [cacheConfig.cacheData, asSection])

  const clearOrder = () => setPageData((c) => ({ ...c, selectedOrder: null }))

  const handleViewOrder = (data) => {
    const id = data
    handPushChildState('orders', id)
    setPageData((p) => ({ ...p, selectedOrder: id }))
  }

  useEffect(() => {
    const tmp = pageData.orders
    if (config.path === 'orders' && config.childPath !== '') {
      const selectedOrder = tmp.find((o) => o.id === config.childPath)
      if (!selectedOrder) return
      setPageData((p) => ({ ...p, selectedOrder: selectedOrder.id }))
      return
    }
    clearOrder()
  }, [config])

  if (pageData.selectedOrder) {
    return (
      <AccountTransaction
        {...{
          selectedOrder: pageData.selectedOrder,
          transactions: cacheConfig.cacheData.Transactions,
          unassignedItems: pageData.unassignedItems,
          clearOrder
        }}
      />
    )
  }

  return (
    <AccountSectionWrapper
      showDivider={false}
      title={accountTranslations.dashboard.orders.title}
      isPage={!asSection}
      mobileCtaBottom={true}
      mobileCtaAlign={'flex-center'}
      cta={{
        display: displayViewAllOrdersButton,
        title: accountTranslations.dashboard.orders.view_all,
        callback: () => {
          handlePushState('orders')
        },
        hook: ''
      }}
    >
      <Suspense
        props={{
          skipLoad: cacheConfig.cacheData.Transactions?.length,
          asyncCallback: async () => {
            const orders = await omneoClient.profileClient.getLedgers()
            const omneoTransactions = await omneoClient.profileClient.getTransactionsQuery()

            // Map Shopify orders to look like Omneo transactions
            const shopifyTransactions = (accountGlobal?.orders ?? [])
              .reduce((acc, order) => {
                // If this shopify order is in the omneo transactions, attach the shopify transaction to the omneo transaction
                const omneoTxIndex = omneoTransactions.findIndex((t) => t.order_number === order.name)
                if (omneoTxIndex !== -1) {
                  omneoTransactions[omneoTxIndex].shopifyTransaction = order
                  return acc
                }

                acc.push({
                  ...order,
                  isShopifyOrder: true,
                  total: (order.total_price ?? 0) / 100,
                  items: order.line_items.map((item) => ({
                    ...item,
                    product_images: [{ url: item.image }]
                  }))
                })

                return acc
              }, [])
              .sort((a, b) => {
                const dateA = new Date(a.created_at)
                const dateB = new Date(b.created_at)
                return dateB - dateA
              })

            // Combine Omneo transactions with Shopify transactions
            const transactions = [...omneoTransactions, ...shopifyTransactions].sort((a, b) => {
              const dateA = new Date(a.created_at)
              const dateB = new Date(b.created_at)
              return dateB - dateA
            })

            const unassignedItems = await omneoClient.profileClient.getUnassignedTransactionItems({ filterOutExpired: false, filterReturned: true })

            cacheConfig.updateCacheConfig('Orders', orders)
            cacheConfig.updateCacheConfig('ShopifyTransactions', shopifyTransactions)
            cacheConfig.updateCacheConfig('Transactions', transactions)
            cacheConfig.updateCacheConfig('UnassignedItems', unassignedItems)

            setPageData((page) => ({
              ...page,
              orders: [...orders],
              unassignedItems
            }))
          }
        }}
      >
        <div className={''}>
          {cacheConfig.cacheData.Transactions?.length ? (
            <TransactionList
              {...{
                handleViewOrder,
                transactions: cacheConfig.cacheData.Transactions,
                shopifyTransactions: cacheConfig.cacheData.ShopifyTransactions,
                renderLimit
              }}
            />
          ) : (
            <NoOrders />
          )}
        </div>

        {displayBanner && (
          <div>
            <AccountBannerTransitions />
          </div>
        )}
      </Suspense>
    </AccountSectionWrapper>
  )
}
